import React, { useState } from 'react';
import './DisputeModal.css';

interface DisputeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (proposedScore: 'success' | 'failed' | 'bad_task', note: string) => void;
  currentScore: 'success' | 'failed';
  problemName: string;
}

const DisputeModal: React.FC<DisputeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentScore,
  problemName
}) => {
  const [proposedScore, setProposedScore] = useState<'success' | 'failed' | 'bad_task' | ''>('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!proposedScore || !note.trim()) {
      alert('Please select a proposed score and provide a note explaining your dispute.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(proposedScore, note);
      // Reset form
      setProposedScore('');
      setNote('');
      onClose();
    } catch (error) {
      console.error('Error submitting dispute:', error);
      alert('Failed to submit dispute. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dispute-modal-overlay" onClick={onClose}>
      <div className="dispute-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dispute-modal-header">
          <h2>Dispute Final Answer</h2>
          <button className="dispute-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="dispute-modal-content">
          <div className="dispute-info">
            <p className="problem-name"><strong>Problem:</strong> {problemName}</p>
            <p className="current-score">
              <strong>Current final score:</strong> 
              <span className={`score-badge ${currentScore}`}>
                {currentScore === 'success' ? '✓ Success' : '✗ Failed'}
              </span>
            </p>
          </div>
          
          <div style={{
            background: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '6px',
            padding: '0.75rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            color: '#92400e',
            lineHeight: '1.4'
          }}>
            <strong>⚠️ Note:</strong> Disputes are reviewed carefully. Valid disputes will improve your accuracy, but incorrect disputes may lower it. Please provide clear evidence for your claim.
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>What do you believe the correct score should be?</label>
              <div className="score-options">
                <button
                  type="button"
                  className={`score-option success ${proposedScore === 'success' ? 'selected' : ''}`}
                  onClick={() => setProposedScore('success')}
                  disabled={currentScore === 'success'}
                >
                  ✓ Success
                </button>
                <button
                  type="button"
                  className={`score-option failed ${proposedScore === 'failed' ? 'selected' : ''}`}
                  onClick={() => setProposedScore('failed')}
                  disabled={currentScore === 'failed'}
                >
                  ✗ Failed
                </button>
                <button
                  type="button"
                  className={`score-option bad-task ${proposedScore === 'bad_task' ? 'selected' : ''}`}
                  onClick={() => setProposedScore('bad_task')}
                >
                  ⚠ Bad Task
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="dispute-note">
                Please explain why you believe the score is incorrect:
              </label>
              <textarea
                id="dispute-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Provide specific details about why the current score is wrong..."
                rows={5}
                required
              />
            </div>
            
            <div className="dispute-modal-actions">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={!proposedScore || !note.trim() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Dispute'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DisputeModal;